import {
  CAPSIZE_DAMPING,
  CAPSIZE_STIFFNESS,
  HEAVE_DAMPING,
  HULL_CHOP_EASE_RATE,
  HULL_SAMPLE_COUNT,
  PITCH_DAMPING,
  PITCH_GAIN,
  PITCH_LIMIT_RAD,
} from './constants';
import type { SeaSpectrum } from './seaSpectrum';

/** The least-squares fit of the water surface under the hull. */
export interface HullSurfaceFit {
  /** Mean surface elevation across the footprint, px. The heave target. */
  meanPx: number;
  /** Best-fit surface slope across the footprint. The pitch target. */
  slope: number;
  /** RMS water left over after the fit: chop the hull bridges rather than rides. */
  residualPx: number;
}

export interface HullTuning {
  /** Centre of the hull, screen px. */
  centreX: number;
  /** Half the hull footprint, px. */
  halfWidthPx: number;
  /** Pitch natural frequency, rad/s. */
  pitchFrequency: number;
  /** Heave natural frequency, rad/s. */
  heaveFrequency: number;
  /** The boat's own vertical band, px. It rides the sea but stays in frame. */
  heaveLimitPx: number;
  /** Broader inland-spectrum blend, 0 calm/coastal to 1 full inland chop. */
  inlandChop: number;
}

/**
 * The hull's response to the sea: heave, pitch and capsize roll.
 *
 * Heave closely follows the local water so the boat stays seated. Pitch is the
 * expressive, resonant degree of freedom, driven by the wave slope across the
 * hull footprint. Capsize roll is a separate spring driven by the app's rating.
 */
export class HullDynamics {
  /** Scratch samples reused by the fit; avoids allocating at 120Hz. */
  private static readonly surfaceSamples = new Float64Array(HULL_SAMPLE_COUNT);

  /** Heave elevation, px (positive up). */
  heavePx = 0;

  /** Heave rate, px/s. Read by the hull churn. */
  heaveRatePxS = 0;

  /** Pitch, radians. */
  pitchRad = 0;

  /** Pitch rate, rad/s. Read by the hull churn. */
  pitchRateRadS = 0;

  /** Smoothed bridged-chop level, 0 to 1. Used only while drawing. */
  chop = 0;

  /** Capsize progress, 0 upright to 1 fully inverted. */
  capsize = 0;

  private capsizeRate = 0;

  /** 1 when the current moment is in the red band. */
  capsizeTarget = 0;

  /** Bow clearance last frame, for slam detection. */
  bowGapPx = 0;

  /** Capsize progress last frame, for detecting the instant it goes over. */
  previousCapsize = 0;

  /**
   * Sample the surface across the hull footprint and fit a line to it.
   *
   * A hull spanning many wavelengths fits a near-flat line, so it bridges chop
   * and platforms; a hull shorter than the wave contours it instead. The
   * length-to-wavelength behaviour emerges from this rather than being coded.
   */
  static fitSurface(
    sea: SeaSpectrum,
    centreX: number,
    halfWidthPx: number,
  ): HullSurfaceFit {
    const n = HULL_SAMPLE_COUNT;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;

    for (let i = 0; i < n; i += 1) {
      const x = -halfWidthPx + (2 * halfWidthPx * i) / (n - 1);
      const y = sea.elevationAt(centreX + x);

      HullDynamics.surfaceSamples[i] = y;
      sumY += y;
      sumXY += x * y;
      sumXX += x * x;
    }

    const meanPx = sumY / n;
    const slope = sumXY / sumXX;
    let residualSquared = 0;

    for (let i = 0; i < n; i += 1) {
      const x = -halfWidthPx + (2 * halfWidthPx * i) / (n - 1);
      const residual = HullDynamics.surfaceSamples[i] - (meanPx + slope * x);
      residualSquared += residual * residual;
    }

    return {
      meanPx,
      slope,
      residualPx: Math.sqrt(residualSquared / n),
    };
  }

  /** Integrate one fixed sub-step. */
  step(dt: number, sea: SeaSpectrum, tuning: HullTuning): void {
    sea.advance(dt);

    const fit = HullDynamics.fitSurface(
      sea,
      tuning.centreX,
      tuning.halfWidthPx,
    );
    const pitchTarget = Math.atan(fit.slope * PITCH_GAIN);
    const { heaveFrequency: wnH, pitchFrequency: wnP, heaveLimitPx } = tuning;

    if (tuning.inlandChop <= 0) {
      // Coastal and calm inland behaviour stays exactly as before.
      this.chop = 0;
    } else {
      const hullRelativeResidual =
        fit.residualPx / Math.max(tuning.halfWidthPx * 0.16, 1);
      const rawChop =
        tuning.inlandChop *
        Math.min(1, Math.max(0, (hullRelativeResidual - 0.08) / 0.92));
      this.chop +=
        (rawChop - this.chop) * Math.min(1, dt * HULL_CHOP_EASE_RATE);
    }

    // Heave is second-order (added mass plus buoyant stiffness) so the hull lags
    // and resonates near its own period rather than being glued to the surface.
    // The excitation is already filtered by the least-squares mean.
    this.heaveRatePxS +=
      (wnH * wnH * (fit.meanPx - this.heavePx) -
        2 * HEAVE_DAMPING * wnH * this.heaveRatePxS) *
      dt;
    this.heavePx += this.heaveRatePxS * dt;

    if (this.heavePx > heaveLimitPx) {
      this.heavePx = heaveLimitPx;
      if (this.heaveRatePxS > 0) {
        this.heaveRatePxS = 0;
      }
    } else if (this.heavePx < -heaveLimitPx) {
      this.heavePx = -heaveLimitPx;
      if (this.heaveRatePxS < 0) {
        this.heaveRatePxS = 0;
      }
    }

    this.pitchRateRadS +=
      (wnP * wnP * (pitchTarget - this.pitchRad) -
        2 * PITCH_DAMPING * wnP * this.pitchRateRadS) *
      dt;
    this.pitchRad += this.pitchRateRadS * dt;

    if (this.pitchRad > PITCH_LIMIT_RAD) {
      this.pitchRad = PITCH_LIMIT_RAD;
      this.pitchRateRadS = 0;
    } else if (this.pitchRad < -PITCH_LIMIT_RAD) {
      this.pitchRad = -PITCH_LIMIT_RAD;
      this.pitchRateRadS = 0;
    }

    // Capsize roll is sprung toward the target (0 upright, 1 inverted), and
    // rights itself if conditions ease back out of the red band.
    this.capsizeRate +=
      (CAPSIZE_STIFFNESS *
        CAPSIZE_STIFFNESS *
        (this.capsizeTarget - this.capsize) -
        2 * CAPSIZE_DAMPING * CAPSIZE_STIFFNESS * this.capsizeRate) *
      dt;
    this.capsize += this.capsizeRate * dt;

    if (this.capsize < 0) {
      this.capsize = 0;
      this.capsizeRate = 0;
    } else if (this.capsize > 1) {
      this.capsize = 1;
      if (this.capsizeRate > 0) {
        this.capsizeRate = 0;
      }
    }
  }

  /**
   * Snap straight to the resting attitude for the current surface, with no
   * animation. This is the reduced-motion path.
   */
  snapTo(sea: SeaSpectrum, tuning: HullTuning): void {
    const fit = HullDynamics.fitSurface(
      sea,
      tuning.centreX,
      tuning.halfWidthPx,
    );

    this.heavePx = Math.max(
      -tuning.heaveLimitPx,
      Math.min(tuning.heaveLimitPx, fit.meanPx),
    );
    this.heaveRatePxS = 0;
    this.pitchRad = Math.atan(fit.slope * PITCH_GAIN);
    this.pitchRateRadS = 0;
    this.chop = 0;
    this.capsize = this.capsizeTarget;
    this.capsizeRate = 0;
  }
}
